import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AidePermission } from '../constants/permissionAide';

interface Props {
  titre: string;
  /** Aide structurée (droit) ou simple texte (module). null → pas d'icône. */
  aide: AidePermission | string | null;
  /** Ligne grisée en pied de bulle. */
  note?: string;
}

const Section: React.FC<{ label?: string; couleur?: string; texte: string }> = ({ label, couleur, texte }) => (
  <Box mt={1}>
    {label && (
      <Typography variant="caption" fontWeight={700} display="block"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', color: couleur }}>
        {label}
      </Typography>
    )}
    <Typography variant="body2" sx={{ color: '#101828' }}>{texte}</Typography>
  </Box>
);

/**
 * Icône ⓘ : l'aide s'ouvre au clic dans une bulle (rien d'affiché tant qu'on ne la
 * demande pas). Le clic ne se propage pas : l'icône peut voisiner une case à cocher.
 */
const AideBulle: React.FC<Props> = ({ titre, aide, note }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!aide) return null;

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Aide : ${titre}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ p: 0.25, color: '#98a2b3', '&:hover': { color: '#2563eb', bgcolor: '#eef4ff' } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 380, p: 1.5, borderRadius: 2, border: '1px solid #e4e7ec' } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#101828' }}>{titre}</Typography>
        {typeof aide === 'string' ? (
          <Section texte={aide} />
        ) : (
          <>
            <Section label="Permet" couleur="#067647" texte={aide.permet} />
            {aide.sans && <Section label="Sans ce droit" couleur="#b42318" texte={aide.sans} />}
            {aide.avec && <Section label="À savoir" couleur="#2563eb" texte={aide.avec} />}
          </>
        )}
        {note && (
          <Typography variant="caption" display="block"
            sx={{ mt: 1.25, pt: 1, borderTop: '1px solid #e4e7ec', color: '#667085' }}>
            {note}
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default AideBulle;
